/* ResearchCoin.jsx — coin detail page per the Bitcoin mockup.
 * Live price via useLiveQuotes where available; everything else honest
 * empty states until the market-data / flow / analyst agents are wired.
 */
import React, { useEffect, useState } from "react";
import "./research-home.css";
import { useResearchLocation, researchNavigate } from "../desk/researchNavigation";
import { useLiveQuotes } from "../desk/useLiveQuotes";
import Sparkline from "./Sparkline";
import { go, fetchFeed, formatMoney, formatBig, chgClass, formatPct, timeAgo, quoteFor, COINS,
  fetchHomeCoins, fetchHomeChart } from "./homeData";

const TABS = ["Overview", "Moves", "Setups", "Flows", "Research"];

function KeyNumbers({ coin }) {
  const c = coin || {};
  const items = [
    ["Market cap", c.market_cap != null ? formatBig(c.market_cap) : null],
    ["24h volume", c.volume_24h != null ? formatBig(c.volume_24h) : null],
    ["All-time high", c.ath != null ? formatMoney(c.ath) : null],
    ["Distance from ATH", c.dist_ath_pct != null ? formatPct(c.dist_ath_pct) : null],
    ["24h change", c.change_24h_pct != null ? formatPct(c.change_24h_pct) : null],
    ["7d change", c.change_7d_pct != null ? formatPct(c.change_7d_pct) : null],
  ];
  return (
    <section aria-label="Key numbers" style={{ marginTop: 18 }}>
      <h3 className="rh-sect">Key Numbers</h3>
      <div className="rh-keynums">
        {items.map(([k, v]) => (
          <div className="rh-keynum" key={k}>
            <div className="rh-k">{k}</div>
            <div className="rh-v" style={v == null ? { color: "var(--rh-faint)" } : undefined}>{v == null ? "—" : v}</div>
          </div>
        ))}
      </div>
      <p className="rh-note" style={{ marginTop: 8 }}>CoinGecko via AEON{c.as_of ? ` · updated ${timeAgo(c.as_of)}` : ""}.</p>
    </section>
  );
}

export default function ResearchCoin() {
  const path = useResearchLocation();
  const sym = decodeURIComponent(path.replace("/research/coins/", "").split("/")[0]).toUpperCase();
  const meta = COINS[sym] || { name: sym, sector: "Crypto" };
  const [tab, setTab] = useState("Overview");
  const [range, setRange] = useState("Month");
  const [feed, setFeed] = useState([]);
  const [coin, setCoin] = useState(null);
  const [chart, setChart] = useState(null);
  const [chartErr, setChartErr] = useState(false);
  const live = useLiveQuotes(true);
  const quote = quoteFor(live?.quotes, sym);

  useEffect(() => {
    let dead = false;
    fetchFeed(40).catch(() => []).then((items) => {
      if (dead) return;
      setFeed(items.filter((it) => (it.assets || []).includes(sym)).slice(0, 6));
    });
    fetchHomeCoins().catch(() => ({ coins: [] })).then((d) => {
      if (dead) return;
      setCoin((d.coins || []).find((c) => c.symbol === sym) || null);
    });
    return () => { dead = true; };
  }, [sym]);

  useEffect(() => {
    let dead = false;
    setChart(null);
    setChartErr(false);
    const days = { Day: 1, Week: 7, Month: 30, Year: 365, Max: 365 }[range] || 30;
    fetchHomeChart(sym, days)
      .then((d) => { if (!dead) setChart(d); })
      .catch(() => { if (!dead) setChartErr(true); });
    return () => { dead = true; };
  }, [sym, range]);

  const coinGo = (s) => (e) => { e.preventDefault(); researchNavigate(`/research/coins/${s}`); };

  return (
    <div className="rh">
      <div className="rh-wrap">
        <nav className="rh-crumb" aria-label="Breadcrumb">
          <a href="/research" onClick={go("/research")}>Research</a> › <a href="/research#rh-markets" onClick={go("/research")}>Coins</a> › {meta.name}
        </nav>
        <div className="rh-coinhead">
          <div>
            <div className="rh-coin-id">
              <span className="rh-coin-badge">{sym.slice(0, 1)}</span>
              <h1>{meta.name} <span className="rh-sym">{sym}</span></h1>
              <span className="rh-sectag">{meta.sector}</span>
            </div>
            <div className="rh-coin-price">
              {coin && coin.price != null ? formatMoney(coin.price) : quote ? formatMoney(quote.price) : "—"}
            </div>
            {coin && coin.change_24h_pct != null && (
              <div className={`rh-chg ${chgClass(coin.change_24h_pct)}`} style={{ fontSize: 15, marginTop: 4 }}>
                {formatPct(coin.change_24h_pct)} <span style={{ color: "var(--rh-faint)" }}>24h</span>
              </div>
            )}
            <div className="rh-asof">{live?.mode === "live" ? "Live" : "Snapshot"}{quote?.asof ? ` · ${timeAgo(quote.asof)}` : " · price feed pending"}</div>
          </div>
          <div className="rh-card rh-rating">
            <h3>AEON Rating</h3>
            <div className="rh-big">[Rating]</div>
            <div className="rh-meter" aria-hidden="true"><i /></div>
            <div className="rh-meter-lbl"><span>Undervalued</span><span>Overvalued</span></div>
            <p className="rh-note" style={{ marginTop: 8 }}>Ratings publish with the analyst agent.</p>
          </div>
        </div>
        <div className="rh-cointabs" role="tablist" aria-label="Coin sections">
          {TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={t === tab}
              className={`rh-tab${t === tab ? " is-active" : ""}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        <div className="rh-article" style={{ paddingTop: 18 }}>
          <div className="rh-article-body">
            {tab === "Overview" && (
              <>
                <div className="rh-card">
                  <div className="rh-chartbox" style={chart && chart.points && chart.points.length >= 2 ? { display: "block", padding: 10 } : undefined}>
                    {chart && chart.points && chart.points.length >= 2 ? (
                      <Sparkline points={chart.points} height={220} />
                    ) : chartErr ? (
                      <>{sym} chart unavailable right now.</>
                    ) : (
                      <>Loading {sym} chart…</>
                    )}
                  </div>
                  <div className="rh-range" role="group" aria-label="Chart range">
                    {["Day", "Week", "Month", "Year", "Max"].map((r) => (
                      <button key={r} type="button" className={r === range ? "is-on" : ""} onClick={() => setRange(r)}>{r}</button>
                    ))}
                  </div>
                </div>
                <KeyNumbers coin={coin} />
                <section aria-label="Current setup" style={{ marginTop: 18 }}>
                  <div className="rh-card">
                    <h3>Current Setup</h3>
                    <div className="rh-empty">Entry zones, invalidation and targets publish with the analyst agent. Not financial advice.</div>
                  </div>
                </section>
              </>
            )}
            {tab !== "Overview" && tab !== "Research" && (
              <div className="rh-empty">{tab} for {sym} fill in once the {tab === "Moves" ? "market-data" : tab === "Flows" ? "flow" : "analyst"} agent is wired.</div>
            )}
            {(tab === "Moves" || tab === "Research") && feed.length > 0 && (
              <section aria-label={`Research on ${meta.name}`} style={{ marginTop: 18 }}>
                <h2 style={{ fontSize: 22, fontWeight: 800 }}>Research on {meta.name}</h2>
                <ul className="rh-newslist">
                  {feed.map((it) => (
                    <li key={it.id}>
                      <div className="rh-thumb" aria-hidden="true" />
                      <div>
                        <div className="rh-newsmeta">{(it.topics && it.topics[0]) || it.publisher || ""}</div>
                        <h4><a href={it.url} target="_blank" rel="noopener noreferrer">{it.title}</a></h4>
                        <div className="rh-newsmeta">{timeAgo(it.publishedAt)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {tab === "Research" && !feed.length && (
              <div className="rh-empty">No research mentions {sym} in the feed yet.</div>
            )}
          </div>
          <aside className="rh-rail">
            <div className="rh-card">
              <h3>AEON Stance</h3>
              <div className="rh-stance-btns" aria-hidden="true"><span>Risk-off</span><span>Neutral</span><span>Risk-on</span></div>
              <p className="rh-note">Stance feed not wired yet.</p>
            </div>
            <div className="rh-card">
              <h3>{sym} Flows</h3>
              <div className="rh-empty">ETF flows, exchange moves and liquidations stream here once the flow agent is wired.</div>
            </div>
            <div className="rh-card">
              <h3>{sym} Catalysts</h3>
              <div className="rh-empty">Unlocks, upgrades and macro dates land here once the calendar feed is wired.</div>
            </div>
            <div className="rh-card">
              <h3>More coins</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Object.keys(COINS).filter((s) => s !== sym).map((s) => (
                  <a key={s} href={`/research/coins/${s}`} onClick={coinGo(s)} className="rh-tab" style={{ fontSize: 12 }}>{s}</a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
